import { useQueryClient, useQuery } from "@tanstack/react-query";
import { useCallback } from "react";

import { ApiError } from "../api/errors";
import { productsApi } from "../api/products";
import type { ApiIssue } from "../api/errors";

export function productKey(productId: string) {
  return ["product", productId] as const;
}

export function useProductDetail(productId: string) {
  return useQuery({
    queryKey: productKey(productId),
    queryFn: ({ signal }) => productsApi.detail(productId, signal),
  });
}

export function useRevisionHistory(productId: string, enabled: boolean) {
  return useQuery({
    queryKey: ["product-revisions", productId],
    queryFn: ({ signal }) => productsApi.revisions(productId, signal),
    enabled,
  });
}

/**
 * What every mutation on this page does afterwards.
 *
 * Success and conflict both end in a reload: after a success the version tokens
 * have moved, and after a conflict the local copy is provably stale. Reloading
 * is the only way the next mutation can carry a version the server will accept.
 */
export function useProductRefresh(productId: string) {
  const client = useQueryClient();
  return useCallback(async () => {
    await Promise.all([
      client.invalidateQueries({ queryKey: productKey(productId) }),
      client.invalidateQueries({ queryKey: ["product-revisions", productId] }),
      client.invalidateQueries({ queryKey: ["products"] }),
      client.invalidateQueries({ queryKey: ["validation", productId] }),
    ]);
  }, [client, productId]);
}

/** Field-keyed issues, so a message can sit beside the input it concerns. */
export function issuesByField(error: unknown): Map<string, ApiIssue[]> {
  const map = new Map<string, ApiIssue[]>();
  if (!(error instanceof ApiError)) return map;
  for (const issue of error.issues) {
    const key = issue.field ?? "";
    const bucket = map.get(key);
    if (bucket) bucket.push(issue);
    else map.set(key, [issue]);
  }
  return map;
}
